import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, Trash2, Pencil, MapPin } from "lucide-react";
import CityStatePicker from "@/components/CityStatePicker";

const BLANK = { label: "Home", line1: "", line2: "", state: "", city: "", pincode: "", phone: "" };

// Amazon/Flipkart-style saved address book. If `onSelect` is provided (checkout),
// selecting a card returns its address; otherwise it's a manage-only list (dashboard).
export default function AddressBook({ selectedId, onSelect, manageOnly = false }) {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/me/addresses").then(({ data }) => {
    setList(data || []);
    if (onSelect && !selectedId && data?.length) {
      const def = data.find((a) => a.is_default) || data[0];
      onSelect(def);
    }
  }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const valid = form.line1 && form.state && form.city && form.pincode && form.phone;
  const save = async () => {
    if (!valid) { toast.error("Fill all required fields"); return; }
    try {
      if (editingId) { await api.put(`/me/addresses/${editingId}`, form); toast.success("Address updated"); }
      else { const { data } = await api.post("/me/addresses", form); toast.success("Address saved"); if (onSelect) onSelect(data); }
      setShowForm(false); setForm(BLANK); setEditingId(null); load();
    } catch (e) { toast.error("Could not save address"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this address?")) return; await api.delete(`/me/addresses/${id}`); toast("Removed"); load(); };
  const makeDefault = async (id) => { await api.post(`/me/addresses/${id}/default`); load(); };
  const startEdit = (a) => { setForm({ label: a.label, line1: a.line1, line2: a.line2, state: a.state, city: a.city, pincode: a.pincode, phone: a.phone }); setEditingId(a.id); setShowForm(true); };

  return (
    <div data-testid="address-book">
      <div className="space-y-3">
        {list.map((a) => {
          const active = onSelect && selectedId === a.id;
          return (
            <div key={a.id} data-testid={`address-${a.id}`}
              onClick={() => onSelect && onSelect(a)}
              className={`border rounded-xl p-4 flex items-start gap-3 ${onSelect ? "cursor-pointer" : ""} ${active ? "border-squire-jeans ring-1 ring-squire-jeans bg-squire-jeans/5" : "border-black/15 hover:border-black/40"}`}>
              {onSelect && <span className={`mt-0.5 w-4 h-4 rounded-full border grid place-items-center ${active ? "border-squire-jeans" : "border-black/30"}`}>{active && <span className="w-2 h-2 rounded-full bg-squire-jeans" />}</span>}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{a.label}</span>
                  {a.is_default && <span className="text-[10px] uppercase tracking-wider bg-black text-white px-1.5 py-0.5 rounded">Default</span>}
                </div>
                <div className="text-sm text-[#4B5563] mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}</div>
                <div className="text-sm text-[#4B5563]">Phone: {a.phone}</div>
                <div className="flex gap-4 mt-2 text-xs">
                  <button onClick={(e) => { e.stopPropagation(); startEdit(a); }} className="text-squire-jeans hover:underline flex items-center gap-1"><Pencil size={12} /> Edit</button>
                  {!a.is_default && <button onClick={(e) => { e.stopPropagation(); makeDefault(a.id); }} className="text-[#4B5563] hover:text-black flex items-center gap-1"><Check size={12} /> Set default</button>}
                  <button onClick={(e) => { e.stopPropagation(); del(a.id); }} className="text-red-600 hover:underline flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!showForm ? (
        <button onClick={() => { setShowForm(true); setForm(BLANK); setEditingId(null); }} data-testid="address-add-new"
          className="mt-3 w-full border border-dashed border-black/25 rounded-xl p-4 text-sm text-[#4B5563] hover:border-black flex items-center justify-center gap-2">
          <Plus size={16} /> Add a new address
        </button>
      ) : (
        <div className="mt-3 border border-black/15 rounded-xl p-5" data-testid="address-form">
          <div className="flex items-center gap-2 mb-4"><MapPin size={16} /> <span className="font-medium">{editingId ? "Edit address" : "New address"}</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (Home / Office)" className="border border-black/20 rounded px-3 py-2.5 text-sm outline-none focus:border-squire-jeans" />
            <div className="hidden md:block" />
            <input value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} placeholder="Address line 1 *" className="border border-black/20 rounded px-3 py-2.5 text-sm outline-none focus:border-squire-jeans md:col-span-2" />
            <input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} placeholder="Address line 2" className="border border-black/20 rounded px-3 py-2.5 text-sm outline-none focus:border-squire-jeans md:col-span-2" />
            <CityStatePicker state={form.state} city={form.city} onState={(v) => setForm((f) => ({ ...f, state: v }))} onCity={(v) => setForm((f) => ({ ...f, city: v }))} />
            <input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} placeholder="Pincode *" className="border border-black/20 rounded px-3 py-2.5 text-sm outline-none focus:border-squire-jeans" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone *" className="border border-black/20 rounded px-3 py-2.5 text-sm outline-none focus:border-squire-jeans" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={save} data-testid="address-save" className="sq-btn-primary">{editingId ? "Update" : "Save"} address</button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-sm text-[#4B5563] hover:text-black">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
